import { Component, ViewChild, TemplateRef } from '@angular/core';
import { DataTableColumn, DataTableSortDirection } from '../../data-table/data-table.component';
import { BadgeVariant } from '../../badge/badge.component';

@Component({
  selector: 'app-data-table-showcase',
  templateUrl: './data-table-showcase.component.html',
  styleUrls: ['./data-table-showcase.component.scss']
})
export class DataTableShowcaseComponent {
  isLoading = false;
  showLoadingDemo = false;
  showEmptyDemo = false;

  // Pagination
  readonly pageSize = 5;
  financialDataPage = 1;
  compactDataPage = 1;
  employeeDataPage = 1;
  permissionsDataPage = 1;

  // Sorting
  financialSortBy = '';
  financialSortDir: DataTableSortDirection = '';
  employeeSortBy = '';
  employeeSortDir: DataTableSortDirection = '';

  @ViewChild('actionButtonTemplate') actionButtonTemplate: TemplateRef<any> | undefined;
  @ViewChild('statusBadgeTemplate') statusBadgeTemplate: TemplateRef<any> | undefined;
  @ViewChild('dateTemplate') dateTemplate: TemplateRef<any> | undefined;
  @ViewChild('canReadTemplate') canReadTemplate: TemplateRef<any> | undefined;
  @ViewChild('canWriteTemplate') canWriteTemplate: TemplateRef<any> | undefined;
  @ViewChild('canDeleteTemplate') canDeleteTemplate: TemplateRef<any> | undefined;

  // Financial data (8 columns - matches practice-manager)
  financialColumns: DataTableColumn[] = [];
  financialColumnsInit: DataTableColumn[] = [
    { field: 'name', header: 'Challenge', align: 'left', tooltip: 'Challenge name and ID', sortable: true },
    { field: 'bookings', header: 'Bookings', align: 'center', prefix: '$', sortable: true },
    { field: 'claims', header: 'Claims', align: 'center', sortable: true },
    { field: 'claimedValue', header: 'Claimed Value', align: 'center', prefix: '$' },
    { field: 'pending', header: 'Pending', align: 'center', prefix: '$', highlight: 'orange' },
    { field: 'pointsEarned', header: 'Points Earned', align: 'center', highlight: 'green', isBold: true, sortable: true }
  ];

  financialData = [
    {
      name: 'Q1 Sales Challenge',
      bookings: '125000',
      participants: 45,
      claims: 38,
      claimedValue: '95000',
      pending: '30000',
      pointsAvailable: 500,
      pointsEarned: 450,
      status: 'Active',
      date: '2026-01-26T18:30:00.000+0000'
    },
    {
      name: 'Q1 Activity Challenge',
      bookings: '85000',
      participants: 52,
      claims: 48,
      claimedValue: '78000',
      pending: '7000',
      pointsAvailable: 400,
      pointsEarned: 380,
      status: 'Completed',
      date: '2026-01-26T18:30:00.000+0000'
    },
    {
      name: 'Winter Promotion',
      bookings: '210000',
      participants: 67,
      claims: 61,
      claimedValue: '185000',
      pending: '25000',
      pointsAvailable: 800,
      pointsEarned: 750,
      status: 'Completed',
      date: '2026-01-26T18:30:00.000+0000'
    },
    {
      name: 'Spring Goals',
      bookings: '95000',
      participants: 38,
      claims: 32,
      claimedValue: '72000',
      pending: '23000',
      pointsAvailable: 350,
      pointsEarned: 310,
      status: 'Pending',
      date: '2026-01-26T18:30:00.000+0000'
    },
    {
      name: 'Summer Initiative',
      bookings: '156000',
      participants: 59,
      claims: 53,
      claimedValue: '142000',
      pending: '14000',
      pointsAvailable: 650,
      pointsEarned: 610,
      status: 'Active',
      date: '2026-01-26T18:30:00.000+0000'
    },
    {
      name: 'Fall Campaign',
      bookings: '118000',
      participants: 44,
      claims: 40,
      claimedValue: '108000',
      pending: '10000',
      pointsAvailable: 480,
      pointsEarned: 460,
      status: 'Pending',
      date: '2026-01-26T18:30:00.000+0000'
    },
    {
      name: 'Holiday Special',
      bookings: '275000',
      participants: 81,
      claims: 76,
      claimedValue: '260000',
      pending: '15000',
      pointsAvailable: 1100,
      pointsEarned: 1050,
      status: 'Completed',
      date: '2026-01-26T18:30:00.000+0000'
    },
    {
      name: 'End Year Boost',
      bookings: '145000',
      participants: 55,
      claims: 49,
      claimedValue: '130000',
      pending: '15000',
      pointsAvailable: 580,
      pointsEarned: 530,
      status: 'Active',
      date: '2026-01-26T18:30:00.000+0000'
    }
  ];

  // Compact variant columns (4 columns)
  compactColumns: DataTableColumn[] = [
    { field: 'name', header: 'Challenge', align: 'left' },
    { field: 'bookings', header: 'Bookings', align: 'center', prefix: '$' },
    { field: 'participants', header: 'Participants', align: 'center' },
    { field: 'pointsEarned', header: 'Points Earned', align: 'center', highlight: 'green', isBold: true }
  ];

  compactData = this.financialData.slice(0, 5);

  // Employee data
  employeeColumns: DataTableColumn[] = [
    { field: 'employeeId', header: 'Employee ID', align: 'left' },
    { field: 'name', header: 'Name', align: 'left', sortable: true },
    { field: 'department', header: 'Department', align: 'center', sortable: true },
    { field: 'salary', header: 'Salary', align: 'right', prefix: '$', sortable: true },
    { field: 'bonus', header: 'Bonus', align: 'right', prefix: '$', highlight: 'green', isBold: true }
  ];

  employeeData = [
    { employeeId: 'EMP001', name: 'John Smith', department: 'Sales', salary: '75000', bonus: '12000' },
    { employeeId: 'EMP002', name: 'Sarah Johnson', department: 'Marketing', salary: '68000', bonus: '8500' },
    { employeeId: 'EMP003', name: 'Michael Chen', department: 'Engineering', salary: '95000', bonus: '15000' },
    { employeeId: 'EMP004', name: 'Emily Davis', department: 'Sales', salary: '70000', bonus: '10500' },
    { employeeId: 'EMP005', name: 'Robert Wilson', department: 'Operations', salary: '65000', bonus: '7000' },
    { employeeId: 'EMP006', name: 'Lisa Anderson', department: 'Engineering', salary: '92000', bonus: '14000' }
  ];

  // Permissions data - with checkbox columns
  permissionsColumns: DataTableColumn[] = [];

  permissionsData = [
    { id: 1, name: 'Admin User', canRead: true, canWrite: true, canDelete: true },
    { id: 2, name: 'Editor', canRead: true, canWrite: true, canDelete: false },
    { id: 3, name: 'Viewer', canRead: true, canWrite: false, canDelete: false },
    { id: 4, name: 'Guest', canRead: false, canWrite: false, canDelete: false },
    { id: 5, name: 'Moderator', canRead: true, canWrite: true, canDelete: true },
    { id: 6, name: 'Analyst', canRead: true, canWrite: false, canDelete: false }
  ];

  private sortData(data: any[], sortBy: string, sortDir: DataTableSortDirection): any[] {
    if (!sortBy || !sortDir) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      const cmp = !isNaN(aNum) && !isNaN(bNum)
        ? aNum - bNum
        : String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  // Paginated data getters
  get paginatedFinancialData(): any[] {
    const sorted = this.sortData(this.financialData, this.financialSortBy, this.financialSortDir);
    const start = (this.financialDataPage - 1) * this.pageSize;
    return sorted.slice(start, start + this.pageSize);
  }

  get paginatedCompactData(): any[] {
    const start = (this.compactDataPage - 1) * this.pageSize;
    return this.compactData.slice(start, start + this.pageSize);
  }

  get paginatedEmployeeData(): any[] {
    const sorted = this.sortData(this.employeeData, this.employeeSortBy, this.employeeSortDir);
    const start = (this.employeeDataPage - 1) * this.pageSize;
    return sorted.slice(start, start + this.pageSize);
  }

  get paginatedPermissionsData(): any[] {
    const start = (this.permissionsDataPage - 1) * this.pageSize;
    return this.permissionsData.slice(start, start + this.pageSize);
  }

  onFinancialSort(event: { sortBy: string; sortDir: DataTableSortDirection }): void {
    this.financialSortBy = event.sortBy;
    this.financialSortDir = event.sortDir;
    this.financialDataPage = 1;
  }

  onEmployeeSort(event: { sortBy: string; sortDir: DataTableSortDirection }): void {
    this.employeeSortBy = event.sortBy;
    this.employeeSortDir = event.sortDir;
    this.employeeDataPage = 1;
  }

  getStatusVariant(status: string): BadgeVariant {
    const map: Record<string, BadgeVariant> = {
      'Active': 'success',
      'Completed': 'info',
      'Pending': 'warning'
    };
    return map[status] ? map[status] : 'secondary';
  }

  ngAfterViewInit(): void {
    this.financialColumns = [
      ...this.financialColumnsInit,
      { field: 'date', header: 'Date', align: 'center', cellTemplate: this.dateTemplate },
      { field: 'status', header: 'Status', align: 'center', cellTemplate: this.statusBadgeTemplate },
      { field: 'name', header: 'Action', align: 'center', cellTemplate: this.actionButtonTemplate }
    ];

    this.permissionsColumns = [
      { field: 'name', header: 'Name', align: 'left', sortable: true },
      { field: 'canRead', header: 'Read', align: 'center', cellTemplate: this.canReadTemplate },
      { field: 'canWrite', header: 'Write', align: 'center', cellTemplate: this.canWriteTemplate },
      { field: 'canDelete', header: 'Delete', align: 'center', cellTemplate: this.canDeleteTemplate }
    ];
  }

  onViewChallenge(row: any): void {
    alert(`View details for: ${row.name}`);
  }

  toggleLoadingDemo(): void {
    this.showLoadingDemo = !this.showLoadingDemo;
    this.isLoading = this.showLoadingDemo;
  }

  toggleEmptyDemo(): void {
    this.showEmptyDemo = !this.showEmptyDemo;
  }

  onCanReadChange(row: any, checked: boolean): void {
    row.canRead = checked;
  }

  onCanWriteChange(row: any, checked: boolean): void {
    row.canWrite = checked;
  }

  onCanDeleteChange(row: any, checked: boolean): void {
    row.canDelete = checked;
  }
}
